import { z } from 'zod'

import { getAccessToken, refreshAccessToken } from '@/lib/auth/store'

/** RFC 7519's `exp`: seconds since the epoch. */
const expiryClaimSchema = z.object({ exp: z.number() })

/** mm-be's SIMPLE_JWT `USER_ID_CLAIM`. */
const userClaimSchema = z.object({ user_id: z.number() })

/**
 * A JWT's payload claims, or `null` when the token is not a JWT whose payload
 * reads as a JSON object.
 *
 * The signature is NOT checked: that is mm-be's job, and nothing here trusts
 * a claim with anything that matters to security. Decoded by hand (base64url
 * → UTF-8 → JSON) because that is all a JWT payload is; a library would add a
 * dependency for three lines.
 */
export const tokenClaims = (token: string): Record<string, unknown> | null => {
  const [, payload] = token.split('.')
  if (payload === undefined || payload === '') return null
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
    const claims: unknown = JSON.parse(new TextDecoder().decode(bytes))
    return typeof claims === 'object' && claims !== null && !Array.isArray(claims)
      ? (claims as Record<string, unknown>)
      : null
  } catch {
    // Not base64url, or not JSON: not a token whose claims can be read.
    return null
  }
}

/**
 * When an access token stops being accepted, in epoch milliseconds, read from
 * its own `exp` claim — or `null` when the token is not a JWT with a numeric
 * `exp`, so the caller keeps today's behaviour instead of guessing. Only ever
 * asks "is sending this pointless?" of the claim (`tokenClaims`).
 */
export const tokenExpiresAt = (token: string): number | null => {
  const parsed = expiryClaimSchema.safeParse(tokenClaims(token))
  return parsed.success ? parsed.data.exp * 1000 : null
}

/** Whose session a token is, from its `user_id` claim, or `null` when it carries none. */
export const tokenUserId = (token: string): number | null => {
  const parsed = userClaimSchema.safeParse(tokenClaims(token))
  return parsed.success ? parsed.data.user_id : null
}

/**
 * A token this close to its `exp` counts as spent: it would lapse in flight,
 * and a device clock a few seconds behind mm-be's would otherwise send it
 * after mm-be has stopped accepting it.
 */
const EXPIRY_SKEW_MS = 30_000

/**
 * Set once a token minted moments ago already reads as spent — the device's
 * clock runs ahead of mm-be's by more than a token's lifetime, so `exp` means
 * nothing here. From then on expiry is left to the 401 path alone, as it was
 * before `exp` was read; otherwise every request would spend a refresh, and a
 * throttled refresh signs the member out.
 */
let clockUntrusted = false

/**
 * Whether sending `token` is pointless: its own `exp` has passed, or is
 * within the skew of passing. False for a token whose `exp` cannot be read,
 * and once the device's clock has proved untrustworthy.
 */
export const isTokenSpent = (token: string): boolean => {
  if (clockUntrusted) return false
  const expiresAt = tokenExpiresAt(token)
  return expiresAt !== null && Date.now() >= expiresAt - EXPIRY_SKEW_MS
}

/**
 * The token a request should carry, refreshed first if it has expired.
 *
 * Not left to the 401 path because not every endpoint answers 401: mm-be's
 * member-aware reads (a course's detail, the courses home) authenticate
 * OPTIONALLY and treat an expired token as no token — a 200 with the
 * anonymous body, which would be cached as the member's own. Concurrent
 * requests share the store's single in-flight refresh. A failed refresh has
 * already signed out (the store does that), so the request goes out bare,
 * exactly as a signed-out one would. A token with no readable `exp` is sent
 * as-is and left to the 401 path.
 *
 * Here rather than in `src/api/client.ts`, which nothing outside `src/api`
 * may import, because the Mitra engine's calls (`askClient.ts`) send the
 * same bearer and owe it the same check.
 */
export const sendableAccessToken = async (): Promise<string | null> => {
  const token = getAccessToken()
  if (token === null || !isTokenSpent(token)) return token
  if (await refreshAccessToken()) {
    const fresh = getAccessToken()
    if (fresh !== null && isTokenSpent(fresh)) clockUntrusted = true
  }
  return getAccessToken()
}
